import React, { useState, useEffect, useRef } from 'react';
import {
  Container, Header, SpaceBetween, Button, Alert, FormField, Input, Textarea, Table, Box, StatusIndicator
} from '@cloudscape-design/components';
import { productImageGenApi } from './productImageGenApi.js';

export default function BrandSetup() {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [brandName, setBrandName] = useState('');
  const [brandWebsite, setBrandWebsite] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [guidelines, setGuidelines] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const logoRef = useRef(null);

  const loadBrands = async () => {
    setLoading(true);
    try { const data = await productImageGenApi.listBrands(); setBrands(data.brands || []); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadBrands(); }, []);

  const handleCreate = async () => {
    if (!brandName.trim()) { setError('Brand name is required'); return; }
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const logos = logoRef.current?.files ? Array.from(logoRef.current.files) : [];
      await productImageGenApi.createBrand(brandName.trim(), guidelines.trim(), brandWebsite.trim(), referenceUrl.trim(), logos);
      setSuccess(`Brand "${brandName}" created successfully 🎉`);
      setBrandName('');
      setBrandWebsite('');
      setReferenceUrl('');
      setGuidelines('');
      if (logoRef.current) logoRef.current.value = '';
      loadBrands();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (brandId) => {
    try { await productImageGenApi.deleteBrand(brandId); loadBrands(); }
    catch (err) { setError(err.message); }
  };

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Create Brand Profile</Header>}>
        <SpaceBetween size="m">
          {success && <Alert type="success" dismissible onDismiss={() => setSuccess('')}>{success}</Alert>}
          {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}

          <FormField label="Brand Name">
            <Input value={brandName} onChange={({ detail }) => setBrandName(detail.value)}
              placeholder="e.g., Nike, Boat, Mamaearth" disabled={creating} />
          </FormField>

          <FormField label="Brand Website (optional)" description="Your brand's own website">
            <Input value={brandWebsite} onChange={({ detail }) => setBrandWebsite(detail.value)}
              placeholder="e.g., https://www.boat-lifestyle.com" disabled={creating} />
          </FormField>

          <FormField label="Reference / Inspiration Website (optional)" description="Competitor or inspiration brand website">
            <Input value={referenceUrl} onChange={({ detail }) => setReferenceUrl(detail.value)}
              placeholder="e.g., https://www.apple.com/shop" disabled={creating} />
          </FormField>

          <FormField label="Brand Guidelines (optional)" description="Describe your brand's visual style, colors, tone">
            <Textarea value={guidelines} onChange={({ detail }) => setGuidelines(detail.value)}
              placeholder="e.g., Minimalist, clean white backgrounds, warm natural lighting..."
              rows={4} disabled={creating} />
          </FormField>

          <FormField label="Logo Images (optional)">
            <input ref={logoRef} type="file" accept="image/*" multiple disabled={creating} />
          </FormField>

          <Button variant="primary" onClick={handleCreate} loading={creating}>
            🏷️ Create Brand Profile
          </Button>
        </SpaceBetween>
      </Container>

      <Container header={
        <Header variant="h2" actions={<Button iconName="refresh" onClick={loadBrands} loading={loading}>Refresh</Button>}>
          Saved Brands
        </Header>
      }>
        <Table
          columnDefinitions={[
            { id: 'name', header: 'Brand', cell: (item) => item.brand_name, width: 200 },
            { id: 'status', header: 'Status', cell: (item) => <StatusIndicator type="success">{item.status}</StatusIndicator>, width: 100 },
            { id: 'created', header: 'Created', cell: (item) => item.created_at ? new Date(item.created_at).toLocaleDateString() : '-', width: 120 },
            { id: 'actions', header: '', cell: (item) => <Button variant="inline-link" onClick={() => handleDelete(item.brand_id)}>Delete</Button>, width: 80 },
          ]}
          items={brands} loading={loading} loadingText="Loading brands..."
          variant="embedded" sortingDisabled
          empty={<Box textAlign="center" padding="l"><b>No brands yet</b><br/>Create a brand profile above to get started.</Box>}
        />
      </Container>
    </SpaceBetween>
  );
}
